
export default function ModularAdvantage() {
  const highlights = [
    {
      icon: 'ri-puzzle-line',
      title: 'Modular Assembly',
      description: 'Each unit breaks down into manageable components that can be transported by hand or small vehicles'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Remote Access',
      description: 'Perfect for mountain tops, islands, forests, and locations where heavy machinery cannot reach'
    },
    {
      icon: 'ri-tools-line',
      title: 'Tool-Free Connection',
      description: 'Innovative quick-connect system allows assembly without specialized tools or equipment'
    },
    {
      icon: 'ri-time-line',
      title: 'Rapid Assembly',
      description: 'Complete unit can be assembled by a small team in just 2-3 days on site'
    }
  ];

  const assemblySteps = [
    {
      step: '01',
      title: 'Transport',
      description: 'Components shipped in standard containers, then transferred to smaller vehicles or carried by hand',
      icon: 'ri-truck-line'
    },
    {
      step: '02',
      title: 'Foundation',
      description: 'Minimal foundation requirements - adjustable feet adapt to uneven terrain',
      icon: 'ri-home-gear-line'
    },
    {
      step: '03',
      title: 'Frame Assembly',
      description: 'Modular steel frame sections connect with precision-engineered locking mechanisms',
      icon: 'ri-building-line'
    },
    {
      step: '04',
      title: 'Panel Installation',
      description: 'Pre-finished wall and roof panels slot into place with weatherproof seals',
      icon: 'ri-layout-grid-line'
    },
    {
      step: '05',
      title: 'Systems Connection',
      description: 'Pre-wired electrical and plumbing modules connect with plug-and-play simplicity',
      icon: 'ri-plug-line'
    },
    {
      step: '06',
      title: 'Final Touches',
      description: 'Interior finishing and quality inspection complete the installation',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  return (
    <section id="features" className="py-20 sm:py-28 lg:py-36 bg-gradient-to-b from-[#000000] via-[#0D1117] to-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-thin mb-6 text-white">
            The <span className="text-cyan-400">Modular</span> Advantage
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Our M Series revolutionizes installation in challenging locations. When cranes cannot reach, 
            our modular design ensures luxury living is still possible anywhere on Earth.
          </p>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-20">
          {highlights.map((highlight, index) => (
            <div 
              key={index}
              className="group relative bg-black/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 hover:border-cyan-500/50 hover:shadow-[0_0_30px_rgba(0,217,255,0.2)] transition-all duration-300"
            >
              <div className="w-14 h-14 bg-cyan-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] transition-all">
                <i className={`${highlight.icon} text-2xl text-black`}></i>
              </div>
              <h3 className="text-xl font-medium text-white mb-3">{highlight.title}</h3>
              <p className="text-base text-gray-400 leading-relaxed">{highlight.description}</p>
            </div>
          ))}
        </div>

        {/* Assembly Process */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl sm:text-4xl font-thin text-white mb-4">
              Assembly <span className="text-cyan-400">Process</span>
            </h3>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From delivery to move-in ready in just days, not months
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {assemblySteps.map((step, index) => (
              <div 
                key={index}
                className="relative bg-black/30 border border-cyan-500/20 rounded-2xl p-6 hover:border-cyan-500/40 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/30 rounded-xl flex items-center justify-center group-hover:bg-cyan-500 group-hover:border-cyan-500 transition-all">
                      <i className={`${step.icon} text-xl text-cyan-400 group-hover:text-black transition-colors`}></i>
                    </div>
                  </div>
                  <div>
                    <div className="text-cyan-400 text-sm font-medium mb-1">Step {step.step}</div>
                    <h4 className="text-lg font-medium text-white mb-2">{step.title}</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feature Image */}
        <div className="relative">
          <div className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px] rounded-3xl overflow-hidden border border-cyan-500/20 shadow-[0_0_40px_rgba(0,217,255,0.15)]">
            <img 
              src="/images/ai-modular-prefabricated-capsule-house-comp-09dd25.webp"
              alt="M Series Assembly"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
            <div className="absolute bottom-8 left-8 right-8 text-white">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-light mb-3">Built for <span className="text-cyan-400">Inaccessible Locations</span></h3>
              <p className="text-base sm:text-lg text-gray-300 max-w-2xl">
                Where helicopters are the only option and cranes cannot reach, 
                the M Series makes luxury living possible.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
